import React, { useCallback, useEffect, useState } from 'react';
import { bool, func, number, object } from 'prop-types';
import styled from 'styled-components';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';

import Tooltip from 'ravenjs/lib/Tooltip';
import { get } from 'ravenjs/utils';

import { AriesIcon, AskHRExpertIcon, StorefrontIcon } from 'components/Icons';
import { DEFAULT_COLORS } from 'constants/colors';
import { ICONS_TOOLTIP_TEXTS, LOGO_ICONS_TEXTS } from 'constants/navigation';
import { EVENTS } from 'constants/events';
import { HR_EXPERT_HELP_PANEL } from 'constants/hrExpertHelp';
import MESSAGES from 'constants/messages';
import Acl from 'modules/acl';
import { APP_PERMISSIONS } from 'constants/permissions';
import { selectors as authSelectors } from 'modules/auth';
import { selectors as userSelectors } from 'modules/user';
import { actions as pendoActions } from 'modules/pendo';
import { shouldHideExperts } from 'utils/user';

import NotificationWidget from '../NotificationWidget/NotificationWidget';
import UserNav from '../UserNav';
import SolutionCenter from '../SolutionCenter';
import AINav from '../AINav';

const NavigationIconsContainer = styled.div`
    display: flex;
    align-items: center;
    height: 40px;
    gap: 12px;
    padding-right: ${({ showDivider }) => (showDivider ? '20px' : '5.125rem')};
    ${({ showDivider }) =>
        showDivider &&
        `
        border-right: 1px solid ${DEFAULT_COLORS.BLACK_00};
    `}
    ${({ theme }) => theme.media.down('md')`
        display: none;
    `};
`;

const NavBarRight = styled.div`
    align-items: center;
    display: flex;
    height: 100%;
`;

const SolutionShopWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 151px;
    height: 40px;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid ${DEFAULT_COLORS.WHITE_3};
    cursor: pointer;
    box-sizing: border-box;

    &:hover {
        background-color: ${DEFAULT_COLORS.BLUE_7};
    }

    /* Focus styles for keyboard navigation */
    &:focus {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
    &:focus:not(:focus-visible) {
        outline: none;
    }
    &:focus-visible {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
`;

const HRExpertWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 170px;
    height: 40px;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid ${DEFAULT_COLORS.WHITE_3};
    cursor: pointer;
    box-sizing: border-box;
    text-decoration: none;

    &:hover {
        background-color: ${DEFAULT_COLORS.BLUE_7};
        text-decoration: none;
    }

    /* Focus styles for keyboard navigation */
    &:focus {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
    &:focus:not(:focus-visible) {
        outline: none;
    }
    &:focus-visible {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
`;

const AriesWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 94px;
    height: 40px;
    max-height: 40px;
    gap: 8px;
    padding: 12px;
    border-radius: 8px;
    border: 1px solid ${DEFAULT_COLORS.WHITE_3};
    cursor: pointer;
    background: ${DEFAULT_COLORS.WHITE};
    box-sizing: border-box;

    &:hover {
        background-color: ${DEFAULT_COLORS.BLUE_7};
    }

    /* Focus styles for keyboard navigation */
    &:focus {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
    &:focus:not(:focus-visible) {
        outline: none;
    }
    &:focus-visible {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
`;

const NotificationWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    max-height: 40px;
    padding: 12px;
    border-radius: 8px;
    border: 1px solid ${DEFAULT_COLORS.WHITE_3};
    cursor: pointer;
    background: ${DEFAULT_COLORS.WHITE};
    box-sizing: border-box;

    &:hover {
        background-color: ${DEFAULT_COLORS.BLUE_7};
    }

    /* Focus styles for keyboard navigation - uses focus-within like AvatarWrapper */
    &:focus-within {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
`;

const AvatarWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    max-height: 40px;
    gap: 8px;
    padding: 9px 12px;
    border-radius: 8px;
    border: 1px solid ${DEFAULT_COLORS.WHITE_3};
    cursor: pointer;
    background: ${DEFAULT_COLORS.WHITE};
    box-sizing: border-box;

    &:hover {
        background-color: ${DEFAULT_COLORS.BLUE_7};
    }

    &:focus-within {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
`;

const IconLabel = styled.span`
    font-family: 'Roboto', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 100%;
    color: ${DEFAULT_COLORS.BLACK};
    white-space: nowrap;
    vertical-align: middle;
`;

// Wrapper to intercept Tooltip's aria-describedby so it doesn't reach the interactive button.
// The Tooltip component always injects aria-describedby onto its direct child via cloneElement.
// Without this wrapper, Chrome reads both aria-label (name+description) AND aria-describedby
// (same description from tooltip), causing duplicate announcements.
// Focus/mouse events bubble through this span to trigger the tooltip correctly.
const TooltipAnchor = styled.span`
    display: inline-flex;
`;

const NavBarIcons = ({
    history,
    userNav,
    user,
    isSoftLogin,
    showDivider,
    sendPendoEvent,
    isNavigationV2Enabled,
    isImpersonatingUser,
    userId,
    userCompany,
}) => {
    const [isNotificationMenuOpen, setIsNotificationMenuOpen] = useState(false);
    const [isTooltipHovered, setIsTooltipHovered] = useState(false);
    const [tooltipAllowed, setTooltipAllowed] = useState(true);

    // When cursor leaves icon area while dropdown is open, disable tooltip
    const handleNotificationMouseLeave = useCallback(() => {
        setIsTooltipHovered(false);
        if (isNotificationMenuOpen) {
            setTooltipAllowed(false);
        }
    }, [isNotificationMenuOpen]);

    // When the dropdown closes, re-enable tooltip for next hover
    useEffect(() => {
        if (!isNotificationMenuOpen) {
            setTooltipAllowed(true);
        }
    }, [isNotificationMenuOpen]);

    // Tooltip shows when cursor is over icon AND tooltip is allowed
    const showNotificationTooltip = isTooltipHovered && tooltipAllowed;

    const onUserNavClick = navLink => {
        const pendoEvent = EVENTS.NAVIGATION.SELECT;

        pendoEvent.eventProperties.selection = `usernav:${navLink.state}`;
        sendPendoEvent(pendoEvent);
    };

    const showContentAi = Acl.check(APP_PERMISSIONS.aiDigitalAssistantView);
    const skus = get(userCompany, 'skus', []);

    const companyHasPremiumSKU = skus => {
        return Boolean(skus.map(({ key }) => key).includes('premium'));
    };

    const companyHasMineralExpertSKU = skus => {
        return Boolean(skus.map(({ key }) => key).includes('askthepro'));
    };

    const isMICoreCompany = skus => {
        return ['mnlintelligence', 'compliance', 'platformlite'].every(element =>
            skus.map(({ key }) => key).includes(element)
        );
    };

    const hasMineralExpertsSKU = companyHasMineralExpertSKU(skus);
    const hasAskTheExpertPermission = Acl.check(APP_PERMISSIONS.askTheExpert);
    const showAskHRExpert =
        hasMineralExpertsSKU && hasAskTheExpertPermission && !shouldHideExperts(user, userCompany);
    const hasMarketplacePermission = Acl.check(APP_PERMISSIONS.systemMarketplace);
    const hasPremiumSKU = companyHasPremiumSKU(skus);
    const hasMICoreSKU = isMICoreCompany(skus);
    const showSolutionsCenterIcon = hasMarketplacePermission && hasPremiumSKU && !hasMICoreSKU;

    const handleSolutionShopClick = () => {
        sendPendoEvent(EVENTS.SOLUTION_SHOP.SOLUTION_SHOP_ICON);
        setTimeout(() => {
            window.location.href = '/solution-shop';
        }, 1000);
    };

    const handleAriesClick = () => {
        window.location.href = '/aries';
    };

    const handleAskHRExpertClick = () => {
        const { pathname = '', search = '' } = get(history, 'location', {});
        history.push('/my-cases/create', {
            src: HR_EXPERT_HELP_PANEL.TOP_NAV_SOURCE,
            sourceUrl: window.location.origin + pathname + search,
        });
    };

    if (!isNavigationV2Enabled) {
        return (
            <NavBarRight>
                {showContentAi && <AINav isDisabled={isImpersonatingUser} />}
                {showSolutionsCenterIcon && <SolutionCenter sendPendoEvent={sendPendoEvent} />}
                <NotificationWidget history={history} key={userId} />
                {userNav.show && (
                    <UserNav
                        history={history}
                        items={isSoftLogin ? {} : userNav.items}
                        onNavClick={navLink => {
                            onUserNavClick(navLink);
                        }}
                        user={user}
                    />
                )}
            </NavBarRight>
        );
    }

    return (
        <NavigationIconsContainer showDivider={showDivider}>
            {showSolutionsCenterIcon && (
                <Tooltip
                    id="tooltip-solution-shop"
                    content={ICONS_TOOLTIP_TEXTS.SOLUTION_SHOP}
                    padding="4px 16px"
                    margin={0}
                    arrowSize={5}
                    placement="bottom"
                    borderRadius="2px"
                    aria-hidden="true"
                >
                    <TooltipAnchor>
                        <SolutionShopWrapper
                            onClick={handleSolutionShopClick}
                            tabIndex={0}
                            // Added a keyboard handler for Enter/Space
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleSolutionShopClick();
                                }
                            }}
                            role="button"
                            aria-label={`Solution Shop. ${ICONS_TOOLTIP_TEXTS.SOLUTION_SHOP}`}
                        >
                            <StorefrontIcon
                                fill={DEFAULT_COLORS.BLACK}
                                width="20px"
                                height="20px"
                                cursor="pointer"
                            />
                            <IconLabel aria-hidden="true">
                                {LOGO_ICONS_TEXTS.SOLUTION_SHOP}
                            </IconLabel>
                        </SolutionShopWrapper>
                    </TooltipAnchor>
                </Tooltip>
            )}

            {showContentAi && (
                <Tooltip
                    id="tooltip-aries"
                    content={
                        isImpersonatingUser
                            ? MESSAGES.RESTRICTED_NAV_ITEM
                            : ICONS_TOOLTIP_TEXTS.ARIES
                    }
                    padding="4px 16px"
                    margin={0}
                    arrowSize={5}
                    placement="bottom"
                    borderRadius="2px"
                    aria-hidden="true"
                >
                    <TooltipAnchor>
                        <AriesWrapper
                            onClick={
                                isImpersonatingUser ? e => e.preventDefault() : handleAriesClick
                            }
                            tabIndex={isImpersonatingUser ? -1 : 0}
                            onKeyDown={
                                isImpersonatingUser
                                    ? undefined
                                    : e => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              handleAriesClick();
                                          }
                                      }
                            }
                            role="button"
                            aria-label={`Aries AI Assistant. ${ICONS_TOOLTIP_TEXTS.ARIES}`}
                            aria-disabled={isImpersonatingUser || undefined}
                            title={isImpersonatingUser ? MESSAGES.RESTRICTED_NAV_ITEM : undefined}
                            style={
                                isImpersonatingUser
                                    ? {
                                          opacity: 0.5,
                                          cursor: 'not-allowed',
                                      }
                                    : undefined
                            }
                        >
                            <AriesIcon
                                fill={DEFAULT_COLORS.CHAT_AVATAR_BASE}
                                width="20px"
                                height="20px"
                                cursor={isImpersonatingUser ? 'not-allowed' : 'pointer'}
                            />
                            <IconLabel aria-hidden="true">{LOGO_ICONS_TEXTS.ARIES}</IconLabel>
                        </AriesWrapper>
                    </TooltipAnchor>
                </Tooltip>
            )}
            {showAskHRExpert && (
                <Tooltip
                    id="tooltip-expert"
                    content={ICONS_TOOLTIP_TEXTS.ASK_AN_EXPERT}
                    placement="bottom"
                    arrowSize={5}
                >
                    <TooltipAnchor>
                        <HRExpertWrapper
                            as="a"
                            href="/my-cases/create"
                            onClick={e => {
                                e.preventDefault();
                                handleAskHRExpertClick();
                            }}
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleAskHRExpertClick();
                                }
                            }}
                            role="button"
                            aria-label={`${LOGO_ICONS_TEXTS.ASK_AN_EXPERT}. ${ICONS_TOOLTIP_TEXTS.ASK_AN_EXPERT}`}
                        >
                            <AskHRExpertIcon
                                fill={DEFAULT_COLORS.BLACK}
                                width="22px"
                                height="22px"
                                cursor="pointer"
                            />
                            <IconLabel aria-hidden="true">
                                {LOGO_ICONS_TEXTS.ASK_AN_EXPERT}
                            </IconLabel>
                        </HRExpertWrapper>
                    </TooltipAnchor>
                </Tooltip>
            )}

            <NotificationWrapper aria-label="Notifications">
                <NotificationWidget
                    history={history}
                    key={userId}
                    onMenuOpenChange={setIsNotificationMenuOpen}
                    tooltipText={ICONS_TOOLTIP_TEXTS.BELL}
                    showTooltip={showNotificationTooltip}
                    onTooltipHover={() => setIsTooltipHovered(true)}
                    onTooltipLeave={handleNotificationMouseLeave}
                />
            </NotificationWrapper>

            {userNav.show && (
                <AvatarWrapper aria-label="User menu">
                    <UserNav
                        history={history}
                        items={isSoftLogin ? {} : userNav.items}
                        onNavClick={navLink => {
                            onUserNavClick(navLink);
                        }}
                        user={user}
                    />
                </AvatarWrapper>
            )}
        </NavigationIconsContainer>
    );
};

NavBarIcons.propTypes = {
    history: object,
    userNav: object,
    user: object,
    isSoftLogin: bool,
    showDivider: bool,
    isImpersonatingUser: bool,
    isNavigationV2Enabled: bool,
    userId: number.isRequired,
    sendPendoEvent: func.isRequired,
    userCompany: object.isRequired,
};

NavBarIcons.defaultProps = {
    history: {},
    userNav: {},
    user: {},
    isSoftLogin: false,
    showDivider: false,
    isImpersonatingUser: false,
    isNavigationV2Enabled: false,
};

const mapStateToProps = createStructuredSelector({
    isImpersonatingUser: authSelectors.isImpersonatingUser,
    userCompany: userSelectors.getUserCompany,
    userPermissions: userSelectors.getUserPermissionsActual,
});

const mapDispatchToProps = {
    sendPendoEvent: pendoActions.sendPendoEvent,
};

export { NavBarIcons as NavBarIconsUnwrapped };
export default connect(mapStateToProps, mapDispatchToProps)(NavBarIcons);
